import { useState, useCallback, useRef, useEffect } from "react";
import { 
  Upload, 
  ArrowLeft,
  ArrowRight,
  Loader2,
  Check,
  Download,
  ShoppingCart,
  BookOpen,
  Crown,
  ChefHat,
  Briefcase,
  Flower2,
  Toilet,
  Droplets,
  Camera,
  Paintbrush,
  X,
  Image,
  CreditCard,
  Truck,
  CheckCircle2,
  Printer,
  Eye,
  ZoomIn,
  Package,
  Share2,
  Link,
  Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const SCENARIOS = [
  { 
    id: "bookworm", 
    name: "Bookworm", 
    description: "Reading a book in a cozy armchair with wine",
    icon: BookOpen,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/abmistfi_bookworm%20Realistic%20style.png"
  },
  { 
    id: "throne_time", 
    name: "Throne Time", 
    description: "On the porcelain throne reading the paper",
    icon: Toilet,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/jsuf1uw0_throne%20time%20realistic.png"
  },
  { 
    id: "master_chef", 
    name: "Master Chef", 
    description: "Cooking up a storm in the kitchen",
    icon: ChefHat,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/3dofs2ai_master%20chef%20realistic.png"
  },
  { 
    id: "the_boss", 
    name: "The Boss", 
    description: "Running the company from the corner office",
    icon: Briefcase,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/zkwuhdf9_boss%20watercolour.png"
  },
  { 
    id: "green_paws", 
    name: "Green Paws", 
    description: "Tending the garden with love",
    icon: Flower2,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/tnxfmqh1_green%20paws%20watercolour.png"
  },
  { 
    id: "royalty", 
    name: "Royalty", 
    description: "Reigning supreme on the royal throne",
    icon: Crown,
    image: "https://customer-assets.emergentagent.com/job_4c9cff6e-d000-475a-89f5-81cf859d671c/artifacts/5czcbi20_royalty%20vintage%20style.png"
  },
];

const ART_STYLES = [
  { 
    id: "watercolor", 
    name: "Watercolor", 
    description: "Soft, artistic watercolor style",
    icon: Droplets,
    color: "from-blue-400 to-cyan-300"
  },
  { 
    id: "realistic", 
    name: "Realistic", 
    description: "Ultra-realistic portrait",
    icon: Camera,
    color: "from-gray-600 to-gray-400"
  },
  { 
    id: "vintage", 
    name: "Vintage", 
    description: "Classical oil painting style",
    icon: Paintbrush,
    color: "from-amber-600 to-yellow-400"
  },
];

const PRODUCTS = [
  { 
    id: "digital", 
    name: "Digital Download", 
    price: 9.99, 
    description: "High-resolution PNG, instant download",
    subtitle: "All 4 portraits included",
    type: "digital"
  },
  { 
    id: "print_12x16", 
    name: "Premium Print 12\"×16\"", 
    price: 24.99, 
    description: "Premium matte paper, museum-quality",
    subtitle: "Free UK shipping + all 4 digital portraits",
    type: "print"
  },
  { 
    id: "print_18x24", 
    name: "Premium Print 18\"×24\"", 
    price: 29.99, 
    description: "Premium matte paper, museum-quality",
    subtitle: "Free UK shipping + all 4 digital portraits",
    type: "print",
    popular: true
  },
  { 
    id: "print_24x36", 
    name: "Premium Print 24\"×36\"", 
    price: 34.99, 
    description: "Premium matte paper, museum-quality",
    subtitle: "Free UK shipping + all 4 digital portraits",
    type: "print"
  },
];

const PET_BREEDS = [
  { id: "dog", name: "Dog (Generic)", category: "Dogs" },
  { id: "schnauzer", name: "Schnauzer", category: "Dogs" },
  { id: "golden_retriever", name: "Golden Retriever", category: "Dogs" },
  { id: "labrador", name: "Labrador", category: "Dogs" },
  { id: "poodle", name: "Poodle / Cockapoo", category: "Dogs" },
  { id: "bulldog", name: "French Bulldog", category: "Dogs" },
  { id: "corgi", name: "Corgi", category: "Dogs" },
  { id: "husky", name: "Husky", category: "Dogs" },
  { id: "beagle", name: "Beagle", category: "Dogs" },
  { id: "dachshund", name: "Dachshund", category: "Dogs" },
  { id: "cat", name: "Cat (Generic)", category: "Cats" },
  { id: "persian_cat", name: "Persian Cat", category: "Cats" },
  { id: "siamese_cat", name: "Siamese Cat", category: "Cats" },
  { id: "tabby_cat", name: "Tabby Cat", category: "Cats" },
  { id: "rabbit", name: "Rabbit", category: "Other" },
  { id: "hamster", name: "Hamster", category: "Other" },
  { id: "bird", name: "Bird", category: "Other" },
];

export const PortraitCreator = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedPrintIndex, setSelectedPrintIndex] = useState(null); // For print orders: which to print
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paidOrderId, setPaidOrderId] = useState(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [remainingGenerations, setRemainingGenerations] = useState(null);
  const [rateLimitHit, setRateLimitHit] = useState(false);
  const [petName, setPetName] = useState(""); // Pet name for personalisation
  
  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // All purchased images (for thank you page)
  const [purchasedImages, setPurchasedImages] = useState([]);
  const [purchasedScenario, setPurchasedScenario] = useState(null);
  const [purchasedPetName, setPurchasedPetName] = useState(null);
  const [isPrintOrder, setIsPrintOrder] = useState(false);
  
  // Shipping address state for print orders
  const [shippingAddress, setShippingAddress] = useState({
    full_name: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    county: "",
    postcode: ""
  });
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shippingErrors, setShippingErrors] = useState({});
  
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Handle payment success/cancel from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const orderId = urlParams.get('order_id');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && orderId && sessionId) {
      // Process payment and get ALL images
      processPaymentSuccess(orderId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      toast.info("Payment cancelled. You can try again when ready.");
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const processPaymentSuccess = async (orderId) => {
    try {
      toast.info("Processing your order...");
      
      // First trigger emails via process-payment
      await axios.post(`${API}/orders/${orderId}/process-payment`);
      
      // Then get ALL images
      const allImagesRes = await axios.get(`${API}/orders/${orderId}/all-images`);
      
      if (allImagesRes.data.images) {
        setPaymentSuccess(true);
        setPaidOrderId(orderId);
        setPurchasedImages(allImagesRes.data.images);
        setPurchasedScenario(allImagesRes.data.scenario);
        setPurchasedPetName(allImagesRes.data.pet_name);
        setSelectedPrintIndex(allImagesRes.data.selected_print_index);
        
        const product = PRODUCTS.find(p => p.id === allImagesRes.data.product_type);
        setIsPrintOrder(product?.type === 'print');
        setSelectedProduct(allImagesRes.data.product_type);
        
        setCurrentStep(6); // Go to success step
        toast.success("Payment successful! Your Pawtraits are ready.");
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      // Fallback to basic verification
      // Fallback to basic verification
      try {
        const orderRes = await axios.get(`${API}/orders/${orderId}`);
        if (orderRes.data.status === 'paid') {
          setPaymentSuccess(true);
          setPaidOrderId(orderId);
          setCurrentStep(6);
          toast.success("Payment successful!");
        } else {
          toast.error("Payment not confirmed. Please contact support.");
        }
      } catch (e) {
        toast.error("Could not verify payment. Please contact support.");
      }
    }
  };

  const verifyPayment = async (sessionId, orderId) => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        await processPaymentSuccess(orderId);
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast.error("Could not verify payment. Please contact support.");
    }
  };

  const validateShippingAddress = () => {
    const errors = {};
    const productInfo = PRODUCTS.find(p => p.id === selectedProduct);
    const isPrint = productInfo?.type === 'print';
    
    if (isPrint) {
      if (!shippingAddress.full_name.trim()) errors.full_name = "Name is required";
      if (!shippingAddress.address_line_1.trim()) errors.address_line_1 = "Address is required";
      if (!shippingAddress.city.trim()) errors.city = "City is required";
      if (!shippingAddress.postcode.trim()) errors.postcode = "Postcode is required";
      
      // Basic UK postcode validation
      const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
      if (shippingAddress.postcode && !postcodeRegex.test(shippingAddress.postcode.trim())) {
        errors.postcode = "Please enter a valid UK postcode";
      }
    }
    
    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckout = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product");
      return;
    }

    const productInfo = PRODUCTS.find(p => p.id === selectedProduct);
    const isPrint = productInfo?.type === 'print';
    
    // For print orders, must select which image to print
    if (isPrint && selectedPrintIndex === null) {
      toast.error("Please select which portrait to print by clicking 'Print This One'");
      return;
    }
    
    // Show shipping form for print orders
    if (isPrint && !showShippingForm) {
      setShowShippingForm(true);
      return;
    }
    
    // Validate shipping for print orders
    if (isPrint && !validateShippingAddress()) {
      toast.error("Please fill in all required address fields");
      return;
    }

    setIsProcessingPayment(true);

    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        job_id: jobId,
        selected_image_index: selectedImageIndex || 0,
        selected_print_index: isPrint ? selectedPrintIndex : null,
        product_type: selectedProduct,
        origin_url: window.location.origin + window.location.pathname,
        email: email,
        shipping_address: isPrint ? {
          full_name: shippingAddress.full_name.trim(),
          address_line_1: shippingAddress.address_line_1.trim(),
          address_line_2: shippingAddress.address_line_2.trim() || null,
          city: shippingAddress.city.trim(),
          county: shippingAddress.county.trim() || null,
          postcode: shippingAddress.postcode.trim().toUpperCase(),
          country: "United Kingdom"
        } : null
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;

    } catch (error) {
      console.error("Checkout error:", error);
      setIsProcessingPayment(false);
      toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
    }
  };

  const downloadSingleImage = async (imageIndex) => {
    if (!paidOrderId) return;
    try {
      toast.info("Preparing download...");
      window.location.href = `${API}/orders/${paidOrderId}/download/${imageIndex}`;
    } catch (error) {
      toast.error("Download failed. Please try again.");
    }
  };

  const downloadAllImages = async () => {
    if (!paidOrderId) return;
    try {
      toast.info("Preparing all portraits as ZIP...");
      window.location.href = `${API}/orders/${paidOrderId}/download-all`;
    } catch (error) {
      toast.error("Download failed. Please try again.");
    }
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB allowed.");
      return;
    }

    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setUploadedImage(e.target.result);
    reader.readAsDataURL(file);
    
    toast.success("Image uploaded successfully!");
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const checkEmailLimit = async () => {
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    
    try {
      const response = await axios.post(`${API}/check-email`, { email });
      if (!response.data.allowed) {
        setRateLimitHit(true);
        setRemainingGenerations(0);
        return false;
      }
      setRemainingGenerations(response.data.remaining);
      setEmailError("");
      return true;
    } catch (error) {
      console.error("Email check error:", error);
      return true; // Allow on error
    }
  };

  const startGeneration = async () => {
    if (!uploadedFile || !selectedScenario || !selectedStyle) {
      toast.error("Please complete all steps first");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setCurrentStep(4);

    try {
      const formData = new FormData();
      formData.append('pet_image', uploadedFile);
      formData.append('scenario', selectedScenario);
      formData.append('art_style', selectedStyle);
      formData.append('email', email);
      if (petName.trim()) {
        formData.append('pet_name', petName.trim());
      }

      const response = await axios.post(`${API}/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newJobId = response.data.job_id;
      setJobId(newJobId);
      setRemainingGenerations(response.data.remaining_today);

      // Poll for status
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/generate/${newJobId}/status`);
          const status = statusRes.data;
          
          setProgress(status.progress || 0);

          if (status.status === 'completed') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            
            // Fetch images
            const imagesRes = await axios.get(`${API}/generate/${newJobId}/images`);
            setGeneratedImages(imagesRes.data.images);
            setIsGenerating(false);
            setCurrentStep(5);
            toast.success("Your preview portraits are ready!");
          } else if (status.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setIsGenerating(false);
            toast.error(status.error || "Generation failed. Please try again.");
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 2000);

    } catch (error) {
      console.error("Generation error:", error);
      setIsGenerating(false);
      if (error.response?.status === 429) {
        setRateLimitHit(true);
        toast.error("You've used your free previews for today!");
      } else {
        toast.error(error.response?.data?.detail || "Failed to start generation. Please try again.");
      }
    }
  };

  const handleAddToCart = async () => {
    if (selectedImageIndex === null || !selectedProduct) {
      toast.error("Please select an image and product");
      return;
    }

    try {
      const response = await axios.post(`${API}/orders`, {
        job_id: jobId,
        selected_image_index: selectedImageIndex,
        product_type: selectedProduct
      });

      toast.success(`Order created! Total: £${response.data.price.toFixed(2)}`);
    } catch (error) {
      console.error("Order error:", error);
      toast.error("Failed to create order. Please try again.");
    }
  };

  const downloadImage = async (imageUrl, index) => {
    if (!jobId) {
      toast.error("No job ID available");
      return;
    }
    
    try {
      toast.info("Starting download...");
      const downloadUrl = `${API}/generate/${jobId}/download/${index}`;
      
      // Use fetch to get the image and create a blob URL
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create and click download link
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = `pawtraits-${selectedScenario || 'portrait'}-${index + 1}.png`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup after a delay
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success("Image downloaded!");
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: direct link
      window.location.href = `${API}/generate/${jobId}/download/${index}`;
    }
  };

  const downloadPdf = () => {
    if (!jobId) {
      toast.error("No job ID available");
      return;
    }
    toast.info("Downloading preview PDF...");
    window.location.href = `${API}/generate/${jobId}/download-pdf`;
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return !!uploadedImage && validateEmail(email) && !rateLimitHit;
      case 2: return !!selectedScenario;
      case 3: return !!selectedStyle;
      case 5: return selectedImageIndex !== null;
      default: return false;
    }
  };

  const goToNext = () => {
    if (currentStep === 3) {
      startGeneration();
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrev = () => {
    if (currentStep > 1 && !isGenerating) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f5f2] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={onBack}
            data-testid="back-to-home-btn"
            className="flex items-center gap-2 text-[#2d2a26]/70 hover:text-[#7c5cff] transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🐾</span>
            <span className="font-bold text-xl text-[#2d2a26]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Pawtraits
            </span>
          </div>
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 md:gap-4 mb-12">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="flex items-center">
              <div 
                data-testid={`step-indicator-${step}`}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                  currentStep === step 
                    ? 'bg-[#7c5cff] text-white scale-110 shadow-[0_0_20px_rgba(124,92,255,0.4)]' 
                    : currentStep > step 
                      ? 'bg-green-500 text-white' 
                      : 'bg-white text-[#2d2a26]/40 border-2 border-[#e6e0dc]'
                }`}
              >
                {currentStep > step ? <Check size={18} /> : step}
              </div>
              {step < 5 && (
                <div className={`hidden md:block w-12 h-1 mx-2 rounded ${
                  currentStep > step ? 'bg-green-500' : 'bg-[#e6e0dc]'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-10">
          {/* Step 1: Upload */}
          {currentStep === 1 && (
            <div className="animate-slide-up">
              <h2 
                className="text-3xl font-bold text-center text-[#2d2a26] mb-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Upload Your Pet's Photo
              </h2>
              <p className="text-center text-[#2d2a26]/60 mb-8">
                Choose a clear photo where your pet's face is visible
              </p>

              <div
                data-testid="upload-dropzone"
                className={`upload-zone rounded-3xl p-12 text-center cursor-pointer transition-all ${
                  isDragging ? 'drag-over' : ''
                } ${uploadedImage ? 'border-green-500 bg-green-50' : ''}`}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileDrop}
                  data-testid="file-input"
                />

                {uploadedImage ? (
                  <div className="space-y-4">
                    <img 
                      src={uploadedImage} 
                      alt="Uploaded pet" 
                      className="max-h-64 mx-auto rounded-2xl shadow-lg"
                    />
                    <p className="text-green-600 font-medium flex items-center justify-center gap-2">
                      <Check size={18} />
                      Photo uploaded! Click to change
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-20 h-20 mx-auto bg-[#e0d8ff] rounded-2xl flex items-center justify-center text-[#7c5cff]">
                      <Upload size={32} />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-[#2d2a26]">
                        Drag & drop your photo here
                      </p>
                      <p className="text-[#2d2a26]/60">
                        or click to browse (max 10MB)
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {uploadedImage && (
                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-[#e0d8ff]/30 rounded-2xl">
                    <p className="text-center text-[#7c5cff] font-medium flex items-center justify-center gap-2">
                      <Check size={18} />
                      Perfect! We'll use this exact photo to create portraits that look like YOUR pet!
                    </p>
                  </div>
                  
                  {/* Pet Name Input (Optional) */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-200">
                    <label className="block text-lg font-semibold text-[#2d2a26] mb-2">
                      🐾 What's your pet's name?
                    </label>
                    <p className="text-sm text-[#2d2a26]/60 mb-4">
                      We'll personalise your portraits with their name! (optional but recommended)
                    </p>
                    <input
                      type="text"
                      value={petName}
                      onChange={(e) => setPetName(e.target.value)}
                      placeholder="Enter your pet's name for a personalised portrait!"
                      data-testid="pet-name-input"
                      className="w-full px-4 py-4 rounded-xl border-2 border-amber-200 focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all text-[#2d2a26] bg-white"
                    />
                    <p className="text-xs text-amber-700 mt-2">
                      ✨ Their name will appear on items like book covers, aprons, desk plaques & more!
                    </p>
                  </div>
                  
                  {/* Email Input */}
                  <div className="bg-white rounded-2xl p-6 border-2 border-[#e6e0dc]">
                    <label className="block text-lg font-semibold text-[#2d2a26] mb-2">
                      Enter your email to get started
                    </label>
                    <p className="text-sm text-[#2d2a26]/60 mb-4">
                      We'll save your portraits so you can order anytime. 3 free previews per day!
                    </p>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                        setRateLimitHit(false);
                      }}
                      placeholder="your@email.com"
                      data-testid="email-input"
                      className={`w-full px-4 py-4 rounded-xl border-2 ${
                        emailError || rateLimitHit ? 'border-red-400' : 'border-[#e6e0dc]'
                      } focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all text-[#2d2a26]`}
                    />
                    {emailError && (
                      <p className="text-red-500 text-sm mt-2">{emailError}</p>
                    )}
                    {rateLimitHit && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-amber-800 font-medium">
                          You've used your free previews for today. Come back tomorrow or order one of your existing portraits!
                        </p>
                      </div>
                    )}
                    {remainingGenerations !== null && remainingGenerations > 0 && (
                      <p className="text-sm text-[#2d2a26]/60 mt-2">
                        {remainingGenerations} free preview{remainingGenerations !== 1 ? 's' : ''} remaining today
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Scenario */}
          {currentStep === 2 && (
            <div className="animate-slide-up">
              <h2 
                className="text-3xl font-bold text-center text-[#2d2a26] mb-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Choose a Scenario
              </h2>
              <p className="text-center text-[#2d2a26]/60 mb-8">
                Pick a fun scenario for your pet's portrait
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {SCENARIOS.map((scenario) => {
                  const Icon = scenario.icon;
                  const isSelected = selectedScenario === scenario.id;
                  
                  return (
                    <div
                      key={scenario.id}
                      data-testid={`scenario-option-${scenario.id}`}
                      onClick={() => setSelectedScenario(scenario.id)}
                      className={`cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 ${
                        isSelected 
                          ? 'ring-4 ring-[#7c5cff] scale-[1.02] shadow-xl' 
                          : 'hover:scale-[1.02] shadow-md hover:shadow-lg'
                      }`}
                    >
                      <div className="aspect-square relative">
                        <img 
                          src={scenario.image}
                          alt={scenario.name}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-[#7c5cff]/20 flex items-center justify-center">
                            <div className="w-12 h-12 bg-[#7c5cff] rounded-full flex items-center justify-center text-white">
                              <Check size={24} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-4 bg-white">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={16} className="text-[#7c5cff]" />
                          <span className="font-semibold text-[#2d2a26]">{scenario.name}</span>
                        </div>
                        <p className="text-xs text-[#2d2a26]/60">{scenario.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Select Art Style */}
          {currentStep === 3 && (
            <div className="animate-slide-up">
              <h2 
                className="text-3xl font-bold text-center text-[#2d2a26] mb-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Choose Art Style
              </h2>
              <p className="text-center text-[#2d2a26]/60 mb-8">
                Select how you'd like your portrait to look
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {ART_STYLES.map((style) => {
                  const Icon = style.icon;
                  const isSelected = selectedStyle === style.id;
                  
                  return (
                    <div
                      key={style.id}
                      data-testid={`style-option-${style.id}`}
                      onClick={() => setSelectedStyle(style.id)}
                      className={`cursor-pointer rounded-2xl p-6 text-center transition-all duration-300 ${
                        isSelected 
                          ? 'bg-[#7c5cff] text-white scale-[1.02] shadow-xl' 
                          : 'bg-[#f8f5f2] hover:bg-[#e0d8ff] text-[#2d2a26]'
                      }`}
                    >
                      <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
                        isSelected ? 'bg-white/20' : 'bg-gradient-to-br ' + style.color
                      }`}>
                        <Icon size={28} className={isSelected ? 'text-white' : 'text-white'} />
                      </div>
                      <h3 className="font-bold text-lg mb-1">{style.name}</h3>
                      <p className={`text-sm ${isSelected ? 'text-white/80' : 'text-[#2d2a26]/60'}`}>
                        {style.description}
                      </p>
                      {isSelected && (
                        <div className="mt-4">
                          <Check size={24} className="mx-auto" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Generating */}
          {currentStep === 4 && isGenerating && (
            <div className="animate-slide-up text-center py-12">
              <div className="w-24 h-24 mx-auto mb-8 relative">
                <div className="absolute inset-0 rounded-full border-4 border-[#e0d8ff]" />
                <div 
                  className="absolute inset-0 rounded-full border-4 border-[#7c5cff] border-t-transparent animate-spin"
                />
                <div className="absolute inset-4 bg-[#f8f5f2] rounded-full flex items-center justify-center">
                  <Loader2 size={32} className="text-[#7c5cff] animate-spin" />
                </div>
              </div>
              
              <h2 
                className="text-3xl font-bold text-[#2d2a26] mb-4"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Creating Your Masterpiece
              </h2>
              <p className="text-[#2d2a26]/60 mb-8">
                Our AI is working its magic. This may take 1-2 minutes...
              </p>

              <div className="max-w-md mx-auto">
                <Progress 
                  value={progress} 
                  className="h-3 mb-2"
                  data-testid="generation-progress"
                />
                <p className="text-sm text-[#2d2a26]/60">{progress}% complete</p>
              </div>

              <div className="mt-8 text-4xl animate-float">
                🎨
              </div>
            </div>
          )}

          {/* Step 5: Results */}
          {currentStep === 5 && !isGenerating && (
            <div className="animate-slide-up">
              <h2 
                className="text-3xl font-bold text-center text-[#2d2a26] mb-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Your Preview Portraits
              </h2>
              
              {/* Preview Notice */}
              <div className="bg-gradient-to-r from-[#7c5cff]/10 to-[#ffb347]/10 rounded-2xl p-4 mb-6 border border-[#7c5cff]/20">
                <p className="text-center text-[#2d2a26] font-medium">
                  ✨ Every order includes <span className="text-[#7c5cff] font-bold">ALL 4 digital portraits FREE!</span>
                </p>
                <p className="text-center text-sm text-[#2d2a26]/60 mt-1">
                  Click any image to enlarge. Watermarks removed after purchase.
                </p>
              </div>

              {/* Generated Images Grid - Protected */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {generatedImages.map((image, index) => {
                  const productInfo = PRODUCTS.find(p => p.id === selectedProduct);
                  const isPrint = productInfo?.type === 'print';
                  const isSelectedForPrint = selectedPrintIndex === index;
                  
                  return (
                    <div
                      key={index}
                      data-testid={`generated-image-${index}`}
                      className={`relative rounded-2xl overflow-hidden border-4 transition-all ${
                        isSelectedForPrint 
                          ? 'border-orange-500 ring-4 ring-orange-200' 
                          : 'border-transparent hover:border-[#e0d8ff]'
                      }`}
                    >
                      {/* Image with protection overlay */}
                      <div 
                        className="aspect-[2/3] relative cursor-pointer select-none"
                        onClick={() => setLightboxImage(image)}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                      >
                        <img 
                          src={image}
                          alt={`Portrait variation ${index + 1}`}
                          className="w-full h-full object-cover pointer-events-none"
                          draggable={false}
                        />
                        {/* Transparent overlay to prevent saving */}
                        <div className="absolute inset-0 bg-transparent" />
                        
                        {/* Variation number */}
                        <div className="absolute top-3 left-3 bg-black/50 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded-full">
                          #{index + 1}
                        </div>
                        
                        {/* Click to enlarge hint */}
                        <div className="absolute bottom-3 left-3 bg-black/50 backdrop-blur text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Eye size={12} />
                          Click to enlarge
                        </div>
                        
                        {isSelectedForPrint && (
                          <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <Printer size={12} />
                            Print This
                          </div>
                        )}
                      </div>
                      
                      {/* Print selection button - only show for print orders */}
                      {isPrint && (
                        <button
                          onClick={() => setSelectedPrintIndex(index)}
                          className={`w-full py-3 font-medium transition-all ${
                            isSelectedForPrint
                              ? 'bg-orange-500 text-white'
                              : 'bg-[#f8f5f2] text-[#2d2a26] hover:bg-orange-100'
                          }`}
                        >
                          {isSelectedForPrint ? '✓ Selected for Print' : 'Print This One'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Sales messaging */}
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6">
                <p className="text-center text-green-800 font-medium text-sm">
                  🎁 <strong>Every purchase includes ALL 4 portraits</strong> as high-res digital downloads!
                  {PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && (
                    <span className="block mt-1 text-green-700">Choose your favourite for printing, keep all 4 digitally!</span>
                  )}
                </p>
              </div>

              {/* Pricing Section */}
              <div className="border-t border-[#e6e0dc] pt-8 mt-8">
                <h3 
                  className="text-2xl font-bold text-center text-[#2d2a26] mb-2"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                >
                  Order Your Pawtraits
                </h3>
                <p className="text-center text-[#2d2a26]/60 mb-6">
                  All 4 portraits included with every purchase!
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {PRODUCTS.map((product) => {
                    const isPrint = product.type === 'print';
                    const isSelected = selectedProduct === product.id;
                    
                    return (
                      <div
                        key={product.id}
                        data-testid={`product-option-${product.id}`}
                        onClick={() => {
                          setSelectedProduct(product.id);
                          // Reset shipping form and print selection when switching products
                          if (!isPrint) {
                            setShowShippingForm(false);
                            setSelectedPrintIndex(null);
                          }
                        }}
                        className={`relative cursor-pointer rounded-2xl p-5 border-2 transition-all ${
                          isSelected
                            ? 'border-[#7c5cff] bg-[#7c5cff] text-white scale-[1.02] shadow-xl'
                            : 'border-[#e6e0dc] hover:border-[#7c5cff] bg-white'
                        }`}
                      >
                        {product.popular && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ffb347] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                            MOST POPULAR
                          </div>
                        )}
                        <div className="text-center">
                          <div className="mb-2">
                            {isPrint ? (
                              <Printer size={24} className={`mx-auto ${isSelected ? 'text-white' : 'text-orange-500'}`} />
                            ) : (
                              <Download size={24} className={`mx-auto ${isSelected ? 'text-white' : 'text-green-500'}`} />
                            )}
                          </div>
                          <h4 className={`font-bold text-sm mb-1 ${isSelected ? 'text-white' : 'text-[#2d2a26]'}`}>
                            {product.name}
                          </h4>
                          <p className={`text-xs mb-2 ${isSelected ? 'text-white/80' : 'text-[#2d2a26]/60'}`}>
                            {product.description}
                          </p>
                          <p className={`text-xs mb-3 ${isSelected ? 'text-white/70' : 'text-green-600'}`}>
                            {product.subtitle}
                          </p>
                          <div className={`text-2xl font-bold ${isSelected ? 'text-white' : 'text-[#7c5cff]'}`}>
                            £{product.price.toFixed(2)}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-3 right-3">
                            <Check size={18} className="text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Print order instructions */}
                {PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && selectedPrintIndex === null && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                    <p className="text-center text-orange-800 font-medium">
                      👆 Select which portrait you'd like printed by clicking "Print This One" above
                    </p>
                  </div>
                )}

                {/* Shipping Address Form for Print Orders */}
                {showShippingForm && PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && (
                  <div className="bg-[#f8f5f2] rounded-2xl p-6 mb-6 animate-slide-up">
                    <div className="flex items-center gap-2 mb-4">
                      <Truck className="text-[#7c5cff]" size={20} />
                      <h4 className="font-bold text-[#2d2a26]">UK Delivery Address</h4>
                      <span className="ml-auto text-xs text-green-600 font-medium">🚚 Free Shipping</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">Full Name *</label>
                        <input
                          type="text"
                          value={shippingAddress.full_name}
                          onChange={(e) => setShippingAddress({...shippingAddress, full_name: e.target.value})}
                          placeholder="John Smith"
                          data-testid="shipping-name"
                          className={`w-full px-4 py-3 rounded-xl border-2 ${shippingErrors.full_name ? 'border-red-400' : 'border-[#e6e0dc]'} focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all`}
                        />
                        {shippingErrors.full_name && <p className="text-red-500 text-xs mt-1">{shippingErrors.full_name}</p>}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">Address Line 1 *</label>
                        <input
                          type="text"
                          value={shippingAddress.address_line_1}
                          onChange={(e) => setShippingAddress({...shippingAddress, address_line_1: e.target.value})}
                          placeholder="123 High Street"
                          data-testid="shipping-address1"
                          className={`w-full px-4 py-3 rounded-xl border-2 ${shippingErrors.address_line_1 ? 'border-red-400' : 'border-[#e6e0dc]'} focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all`}
                        />
                        {shippingErrors.address_line_1 && <p className="text-red-500 text-xs mt-1">{shippingErrors.address_line_1}</p>}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">Address Line 2</label>
                        <input
                          type="text"
                          value={shippingAddress.address_line_2}
                          onChange={(e) => setShippingAddress({...shippingAddress, address_line_2: e.target.value})}
                          placeholder="Flat 2B (optional)"
                          data-testid="shipping-address2"
                          className="w-full px-4 py-3 rounded-xl border-2 border-[#e6e0dc] focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">City *</label>
                        <input
                          type="text"
                          value={shippingAddress.city}
                          onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                          placeholder="London"
                          data-testid="shipping-city"
                          className={`w-full px-4 py-3 rounded-xl border-2 ${shippingErrors.city ? 'border-red-400' : 'border-[#e6e0dc]'} focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all`}
                        />
                        {shippingErrors.city && <p className="text-red-500 text-xs mt-1">{shippingErrors.city}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">County</label>
                        <input
                          type="text"
                          value={shippingAddress.county}
                          onChange={(e) => setShippingAddress({...shippingAddress, county: e.target.value})}
                          placeholder="Greater London (optional)"
                          data-testid="shipping-county"
                          className="w-full px-4 py-3 rounded-xl border-2 border-[#e6e0dc] focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">Postcode *</label>
                        <input
                          type="text"
                          value={shippingAddress.postcode}
                          onChange={(e) => setShippingAddress({...shippingAddress, postcode: e.target.value.toUpperCase()})}
                          placeholder="SW1A 1AA"
                          data-testid="shipping-postcode"
                          className={`w-full px-4 py-3 rounded-xl border-2 ${shippingErrors.postcode ? 'border-red-400' : 'border-[#e6e0dc]'} focus:border-[#7c5cff] focus:ring-4 focus:ring-[#7c5cff]/20 outline-none transition-all`}
                        />
                        {shippingErrors.postcode && <p className="text-red-500 text-xs mt-1">{shippingErrors.postcode}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-[#2d2a26]/70 mb-1">Country</label>
                        <input
                          type="text"
                          value="United Kingdom"
                          disabled
                          className="w-full px-4 py-3 rounded-xl border-2 border-[#e6e0dc] bg-gray-100 text-[#2d2a26]/60"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-[#2d2a26]/50 mt-4">
                      * Required fields. We only ship to UK addresses.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  data-testid="checkout-btn"
                  disabled={!selectedProduct || isProcessingPayment || (PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && selectedPrintIndex === null)}
                  className="w-full bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full py-6 text-lg shadow-[0_8px_30px_rgb(124,92,255,0.3)] hover:shadow-[0_8px_30px_rgb(124,92,255,0.5)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isProcessingPayment ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2" size={20} />
                      {!selectedProduct 
                        ? "Select a product"
                        : PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && selectedPrintIndex === null
                          ? "Select a portrait to print first"
                          : PRODUCTS.find(p => p.id === selectedProduct)?.type === 'print' && !showShippingForm
                            ? "Enter Delivery Address"
                            : `Pay Now — £${PRODUCTS.find(p => p.id === selectedProduct)?.price.toFixed(2)}`
                      }
                    </>
                  )}
                </Button>
                
                <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm text-[#2d2a26]/50">
                  <span className="flex items-center gap-1">
                    <Check size={14} className="text-green-500" />
                    Secure payment
                  </span>
                  <span className="flex items-center gap-1">
                    <Package size={14} className="text-green-500" />
                    All 4 portraits included
                  </span>
                  <span className="flex items-center gap-1">
                    <Truck size={14} className="text-green-500" />
                    Free UK shipping
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Payment Success - Thank You Page */}
          {currentStep === 6 && paymentSuccess && (
            <div className="animate-slide-up py-8">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle2 size={40} className="text-white" />
                </div>
                
                <h2 
                  className="text-3xl md:text-4xl font-bold text-[#2d2a26] mb-3"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                >
                  Thank you! Your Pawtraits are ready 🐾
                </h2>
                <p className="text-[#2d2a26]/60 max-w-md mx-auto">
                  {purchasedPetName && purchasedScenario 
                    ? `${purchasedPetName}'s "${purchasedScenario}" portraits are ready!`
                    : purchasedScenario 
                      ? `Your "${purchasedScenario}" portraits are ready!`
                      : "Your portraits are ready!"}
                </p>
              </div>

              {/* Print Order Notice */}
              {isPrintOrder && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-6 mb-8 max-w-2xl mx-auto">
                  <div className="flex items-center justify-center gap-2 mb-3 text-orange-600">
                    <Truck size={24} />
                    <span className="font-bold text-lg">Print Order Confirmed!</span>
                  </div>
                  <p className="text-[#2d2a26]/70 text-center mb-2">
                    Portrait #{(selectedPrintIndex || 0) + 1} is being prepared for printing on premium matte paper.
                  </p>
                  <p className="text-[#2d2a26]/70 text-center text-sm">
                    Free UK shipping • 3-5 working days delivery • Tracking email sent
                  </p>
                </div>
              )}

              {/* All 4 Portraits Grid */}
              <div className="max-w-4xl mx-auto mb-8">
                <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-3">
                  <h3 className="font-bold text-[#2d2a26] text-lg">
                    All Your Portraits ({purchasedImages.length})
                  </h3>
                  <Button
                    onClick={downloadAllImages}
                    data-testid="download-all-btn"
                    className="bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full"
                  >
                    <Download className="mr-2" size={18} />
                    Download All as ZIP
                  </Button>
                </div>
                
                {/* Mobile save tip */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 sm:hidden">
                  <p className="text-blue-700 text-sm text-center">
                    📱 <strong>Tip:</strong> Tap and hold any image, then select "Save to Photos" to add to your gallery
                  </p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {purchasedImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <div className={`rounded-2xl overflow-hidden border-4 ${
                        isPrintOrder && selectedPrintIndex === index 
                          ? 'border-orange-500 ring-4 ring-orange-200' 
                          : 'border-white shadow-lg'
                      }`}>
                        <div className="aspect-[2/3] relative">
                          <img 
                            src={img.image_data}
                            alt={`Portrait ${index + 1}`}
                            className="w-full h-full object-cover"
                            data-testid={`purchased-image-${index}`}
                          />
                          <div className="absolute top-2 left-2 bg-black/50 backdrop-blur text-white text-xs font-bold px-2 py-1 rounded-full">
                            #{index + 1}
                          </div>
                          {isPrintOrder && selectedPrintIndex === index && (
                            <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                              <Printer size={10} />
                              Print
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-xs">Full Resolution</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => downloadSingleImage(index)}
                        data-testid={`download-single-${index}`}
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 rounded-full text-xs"
                      >
                        <Download size={14} className="mr-1" />
                        Download #{index + 1}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email Confirmation Notice */}
              <div className="bg-amber-50 rounded-xl p-4 mb-6 max-w-md mx-auto border border-amber-100">
                <p className="text-amber-800 text-sm text-center flex items-center justify-center gap-2">
                  <span>📧</span>
                  <span>Confirmation email sent with all download links!</span>
                </p>
              </div>

              {/* Social Sharing Section */}
              <div className="max-w-md mx-auto mb-6">
                <div className="text-center mb-4">
                  <p className="text-[#2d2a26]/70 text-sm flex items-center justify-center gap-2">
                    <Share2 size={16} />
                    Share your AI masterpiece with friends!
                  </p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  {/* Facebook Share */}
                  <button
                    onClick={() => {
                      const shareText = encodeURIComponent("Check out what AI did to my pet! 🐾");
                      const shareUrl = encodeURIComponent("https://pawtraitspets.com");
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}`, '_blank', 'width=600,height=400');
                    }}
                    data-testid="share-facebook-btn"
                    className="w-12 h-12 rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    title="Share on Facebook"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>

                  {/* Instagram Share (opens Instagram app/website) */}
                  <button
                    onClick={() => {
                      // Instagram doesn't have direct share URL, so we copy the link and prompt user
                      navigator.clipboard.writeText("Check out what AI did to my pet! 🐾 https://pawtraitspets.com");
                      toast.success("Link copied! Open Instagram and paste in your story or post 📸");
                    }}
                    data-testid="share-instagram-btn"
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    title="Share on Instagram"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </button>

                  {/* WhatsApp Share */}
                  <button
                    onClick={() => {
                      const shareText = encodeURIComponent("Check out what AI did to my pet! 🐾 https://pawtraitspets.com");
                      window.open(`https://wa.me/?text=${shareText}`, '_blank');
                    }}
                    data-testid="share-whatsapp-btn"
                    className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    title="Share on WhatsApp"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </button>

                  {/* Copy Link */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText("Check out what AI did to my pet! 🐾 https://pawtraitspets.com");
                      toast.success("Link copied to clipboard! 📋");
                    }}
                    data-testid="share-copy-link-btn"
                    className="w-12 h-12 rounded-full bg-[#2d2a26] hover:bg-[#3d3a36] text-white flex items-center justify-center transition-all hover:scale-110 shadow-md"
                    title="Copy Link"
                  >
                    <Copy size={20} />
                  </button>
                </div>
              </div>

              {/* Create Another */}
              <div className="max-w-md mx-auto">
                <Button
                  onClick={() => {
                    setCurrentStep(1);
                    setPaymentSuccess(false);
                    setPaidOrderId(null);
                    setUploadedImage(null);
                    setUploadedFile(null);
                    setSelectedScenario(null);
                    setSelectedStyle(null);
                    setGeneratedImages([]);
                    setSelectedImageIndex(null);
                    setSelectedPrintIndex(null);
                    setSelectedProduct(null);
                    setPurchasedImages([]);
                    setPurchasedScenario(null);
                    setPurchasedPetName(null);
                    setIsPrintOrder(false);
                    setPetName("");
                    setShowShippingForm(false);
                    setShippingAddress({
                      full_name: "",
                      address_line_1: "",
                      address_line_2: "",
                      city: "",
                      county: "",
                      postcode: ""
                    });
                  }}
                  variant="outline"
                  data-testid="create-another-btn"
                  className="w-full rounded-full border-2 border-[#e6e0dc] hover:border-[#7c5cff] hover:bg-[#7c5cff]/5 transition-all"
                >
                  🐾 Create Another Pawtrait
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {!isGenerating && currentStep < 6 && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-[#e6e0dc]">
              <Button
                variant="ghost"
                onClick={goToPrev}
                data-testid="prev-step-btn"
                disabled={currentStep === 1}
                className="rounded-full px-6 disabled:opacity-30"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>

              {currentStep < 5 && (
                <Button
                  onClick={goToNext}
                  data-testid="next-step-btn"
                  disabled={!canProceed()}
                  className="bg-[#7c5cff] hover:bg-[#6a4de6] text-white rounded-full px-8 shadow-[0_8px_30px_rgb(124,92,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {currentStep === 3 ? 'Generate Portrait' : 'Continue'}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Lightbox Modal for Preview Images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
          >
            <X size={32} />
          </button>
          <div 
            className="relative max-w-4xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage}
              alt="Preview - Full View"
              className="w-full h-full object-contain rounded-lg select-none"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur text-white text-sm px-4 py-2 rounded-full">
              Preview - Watermark removed after purchase
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
